import SettingsIcon from '@mui/icons-material/Settings';
import { Box, TablePagination } from '@mui/material';
import {
  DataGridPro,
  type DataGridProProps,
  type GridColDef,
  type GridColumnGroupingModel,
  type GridPaginationModel,
  GridRowParams,
  GridToolbarColumnsButton,
  type GridApiPro,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import type { GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import { isNotNullOrUndefined, withDefault } from '@vy/utils';
import { useCallback, useMemo, useState } from 'react';
import { getSelectedRowIds } from './vy-table-selection';
import { useTableColumnsManager } from '../../../../hooks/use-table-columns-manager';
import { EmptyTableOverlay } from '../../../design/EmptyTableOverlay';
import { EXPANDABLE_ITEM_CLASSNAME, HOVERED_ROW_CLASSNAME, SUB_ITEM_CLASSNAME } from './constants';
import { Spinner } from '../../../design/Spinner';
import { hideColumnSeparatorsSx, suppressPinnedColumnSeparatorSx } from './pinned-columns-sx';
import { VY_TABLE_FUNCTIONAL_COLUMNS, type VyTableProps } from './vy-table-types';

export type VyTableMinimalProps<Item extends Omit<GridValidRowModel, 'rows'>> = Omit<
  VyTableProps<Item>,
  'rows' | 'columns'
>;

export function VyTable<Item extends Omit<GridValidRowModel, 'rows'>>({
  itemsCount,
  items,
  loadingItems = false,
  columns,
  showPagination = true,
  fadeOverlay = false,
  useColumnVisibility = false,
  checkboxVisibleOnHover = false,
  sortingMode = 'server',
  emptyTableMessageSize,
  noRowsOverlay = EmptyTableOverlay,
  rowsPerPageOptions = [10, 25, 50, 100, 500],
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  paginationMode = 'server',
  apiRef: apiRefFromProps,
  tableName,
  tableColumnsApiConfig,
  ...props
}: VyTableProps<Item>) {
  const [hoveredRowId, setHoveredRowId] = useState<GridRowId>();

  const fallbackApiRef = useGridApiRef();
  const apiRef = useMemo(() => apiRefFromProps ?? fallbackApiRef, [apiRefFromProps, fallbackApiRef]);

  const checkboxVisibilitySx = useMemo(
    () =>
      checkboxVisibleOnHover
        ? {
          '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
            visibility: 'hidden',
          },
          '& .MuiDataGrid-row:hover .MuiCheckbox-root': {
            visibility: 'visible',
          },
          '& .Mui-selected .Mui-checked': {
            visibility: 'visible',
          },
        }
        : {},
    [checkboxVisibleOnHover],
  );

  const getRowClassName = useCallback(
    (params: Parameters<NonNullable<DataGridProProps['getRowClassName']>>[0]) => {
      if (params.row.isChild) {
        return SUB_ITEM_CLASSNAME;
      }
      if (isNotNullOrUndefined(params.row.isParent)) {
        return EXPANDABLE_ITEM_CLASSNAME;
      }
      if (params.id === hoveredRowId) {
        return HOVERED_ROW_CLASSNAME;
      }

      return '';
    },
    [hoveredRowId],
  );

  const generateColumns = useMemo((): GridColDef<Item>[] => {
    if (!useColumnVisibility) {
      return columns;
    }

    if (columns[0]?.field === VY_TABLE_FUNCTIONAL_COLUMNS.PREFIX) {
      const currColumns = [...columns];
      currColumns[0] = {
        ...currColumns[0],
        field: VY_TABLE_FUNCTIONAL_COLUMNS.PREFIX,
        headerName: 'prefixHeader',
        renderHeader: CustomToolbar,
        headerAlign: 'center',
        sortable: false,
        hideable: false,
      };

      return currColumns;
    }

    return [
      {
        field: VY_TABLE_FUNCTIONAL_COLUMNS.PREFIX,
        headerName: 'prefixHeader',
        renderHeader: CustomToolbar,
        headerAlign: 'center',
        sortable: false,
        hideable: false,
      },
      ...columns,
    ];
  }, [columns, useColumnVisibility]);

  const { processedColumns } = useTableColumnsManager({
    apiRef: apiRef as React.MutableRefObject<GridApiPro>,
    columns: generateColumns,
    tableName,
    apiConfig: tableColumnsApiConfig,
  });

  const dataGridSx = useMemo(
    () => ({
      border: 'none',
      [`& .${SUB_ITEM_CLASSNAME}`]: {
        backgroundColor: '#F5F5F5',
      },
      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
      {
        outline: 'none',
      },
      '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        display: 'none',
      },
      // MUI X v9 stopped painting the grid body white; the theme's
      // background.paper (#E8EBF2) shows through wherever cells don't
      // cover — empty area below the last row, the filler cells beside
      // narrow columns, the scrollbar corner. Paint the whole DataGrid
      // container white so every transparent sub-element inherits it.
      // The filler/pinnedRight-filler and scrollbarFiller each set their
      // own background on top, so they need explicit white too.
      '& .MuiDataGrid-main, & .MuiDataGrid-filler, & .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-scrollbarFiller': {
        backgroundColor: '#FFFFFF',
      },
      '& .MuiDataGrid-row:hover': {
        cursor: 'pointer',
      },
      [`& .${EXPANDABLE_ITEM_CLASSNAME}:hover`]: {
        backgroundColor: 'inherit',
      },
      '& .MuiDataGrid-row .MuiDataGrid-cellCheckbox .Mui-disabled ': {
        display: 'none',
      },
      [`& .${HOVERED_ROW_CLASSNAME}`]: {
        backgroundColor: '#F5F5F5',
      },
      ...suppressPinnedColumnSeparatorSx,
      ...hideColumnSeparatorsSx,
      // MUI X v9 cells default to display:block with line-height:<rowHeight> to
      // vertically centre inline text. renderCell content with block children
      // (e.g. the Amount column's <Price> <p>) ignores that and sticks to the top.
      // Restore v6's flex-centred cells — v9's --textLeft/Center/Right classes set
      // justify-content too, so horizontal alignment from `align` still works.
      '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 'normal',
      },
      '& .MuiDataGrid-cell[data-field="actionMenu"], & .MuiDataGrid-columnHeader[data-field="actionMenu"]': {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& .MuiDataGrid-cell[data-field="actionMenu"] .MuiIconButton-root:hover': {
        backgroundColor: 'transparent',
      },
      // Invoice tables: subtle divider between the status column and the checkbox column.
      // Lives here because the InvoicesTable/VyTableWithFilters sx props are overridden
      // by their `{...props}` spreads; dataGridSx is the only reliably-applied sx.
      '& .MuiDataGrid-cell[data-field="billingStatus"]': {
        borderLeft: '1px solid #DDDDDD',
      },
      '& .MuiDataGrid-withBorderColor': {
        background: '#fff',
        boxShadow: 'none',
      },
      ...checkboxVisibilitySx,
    }),
    [checkboxVisibilitySx],
  );

  const handleRowClick = useCallback(
    (
      params: Parameters<NonNullable<DataGridProProps['onRowClick']>>[0],
      event: Parameters<NonNullable<DataGridProProps['onRowClick']>>[1],
      details: Parameters<NonNullable<DataGridProProps['onRowClick']>>[2],
    ) => {
      setHoveredRowId(params.id);
      props.onRowClick?.(params, event, details);
    },
    [props.onRowClick],
  );

  // MUI X v9 changed the row-selection model from `GridRowId[]` to
  // `{ type: 'include' | 'exclude'; ids: Set }`. The header "select all"
  // checkbox emits `{ type: 'exclude', ids: <deselected> }`, which consumers
  // reading `model.ids` as the selected set (the common pattern) interpret as
  // an empty selection — so select-all silently did nothing. Normalize every
  // model to the resolved `include` form before handing it to consumers.
  const handleRowSelectionModelChange = useCallback<
    NonNullable<DataGridProProps['onRowSelectionModelChange']>
  >(
    (model, details) => {
      if (!props.onRowSelectionModelChange) {
        return;
      }
      const selectedIds = getSelectedRowIds(model, apiRef);
      props.onRowSelectionModelChange({ type: 'include', ids: new Set(selectedIds) }, details);
    },
    [props.onRowSelectionModelChange, apiRef],
  );

  const handlePageChange = useCallback(
    (_event: unknown, newPage: number) => {
      onPageChange?.(newPage);
    },
    [onPageChange],
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newPageSize = Number(event.target.value);
      onPageSizeChange?.(newPageSize);
      onPageChange?.(0);
    },
    [onPageChange, onPageSizeChange],
  );

  const rowsPerPage = useMemo(() => {
    return withDefault(pageSize, rowsPerPageOptions[0]);
  }, [pageSize, rowsPerPageOptions]);

  return (
    <Box
      {...getSelectorAttribute(vyTableSelectors.root)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        pinnedColumns={{
          right: ['actionMenu'],
        }}
        loading={loadingItems}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        density="standard"
        getRowClassName={getRowClassName}
        paginationMode={paginationMode}
        rowCount={itemsCount}
        {...props}
        localeText={{
          toolbarColumns: ' ',
        }}
        columns={processedColumns}
        rows={items}
        // sx is an array (not an object spread) so consumer `sx` merges with
        // the base styles at the property level. An object spread lets a
        // consumer's `'& .MuiDataGrid-cell'` key wholesale-replace the base
        // cell rule — which silently dropped the v9 flex-centering and left
        // renderCell content (e.g. chips) stuck to the top of the cell.
        sx={[
          dataGridSx,
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
          {
            flex: 1,
            minHeight: 0,
            position: 'relative',
            ...(fadeOverlay && {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40px',
                height: '100%',
                background:
                  'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, #FFFFFF 100%)',
                pointerEvents: 'none',
                zIndex: 1,
              },
            }),
          },
        ]}
        slots={{
          noRowsOverlay,
          loadingOverlay: NoOpOverlay,
          ...props.slots,
        }}
        slotProps={{
          noRowsOverlay: {
            defaultValue: emptyTableMessageSize,
          },
          row: {
            ...getSelectorAttribute(vyTableSelectors.row),
            ...props.slotProps?.row,
            onMouseOver: (event) => {
              setHoveredRowId(undefined);
              props.slotProps?.row?.onMouseOver?.(event);
            },
          },
        }}
        sortingMode={sortingMode}
        onRowClick={handleRowClick}
        onRowSelectionModelChange={handleRowSelectionModelChange}
      />

      {loadingItems && <TableLoadingOverlay />}

      {showPagination && (
        <TablePagination
          {...getSelectorAttribute(vyTableSelectors.pagination)}
          component="div"
          className={props.paginationClassName}
          sx={{
            borderTop: '1px solid #E7E8E9',
            flexShrink: 0,
          }}
          count={itemsCount}
          page={withDefault(page, 0)}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Box>
  );
}

function CustomToolbar() {
  return (
    <GridToolbarColumnsButton
      slotProps={{
        button: {
          startIcon: (
            <SettingsIcon
              sx={{
                color: '#323232',
                height: '18px',
                width: '24px',
              }}
            />
          ),
          style: {
            minWidth: '48px',
          },
        },
      }}
    />
  );
}

type VyTableLiteProps<Item extends GridValidRowModel> = {
  items: Item[];
  columns: GridColDef<Item>[];
  loadingItems?: boolean;
  onRowClick?: (params: GridRowParams<Item>) => void;
  getRowId?: (row: Item) => GridRowId;
  apiRef?: React.MutableRefObject<GridApiPro | null>;
  pinnedRows?: { top?: readonly Item[]; bottom?: readonly Item[] };
  pinnedColumns?: { left?: string[]; right?: string[] };
  density?: 'standard' | 'comfortable' | 'compact';
  /** Sets initial hidden/visible state; user can still toggle columns */
  initialColumnVisibility?: Record<string, boolean>;
  /** Controls column visibility externally (overrides user toggles) */
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
  columnGroupingModel?: GridColumnGroupingModel;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  pageSizeOptions?: number[];
};

export function VyTableLite<Item extends GridValidRowModel>({
  items,
  columns,
  loadingItems,
  onRowClick,
  getRowId,
  apiRef,
  pinnedRows,
  pinnedColumns,
  density,
  initialColumnVisibility,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnGroupingModel,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions,
}: VyTableLiteProps<Item>) {
  const resolvedPageSizeOptions = pageSizeOptions ?? [25, 50, 100, 500];

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {loadingItems && <TableLoadingOverlay />}
      <DataGridPro
        apiRef={apiRef}
        rows={items}
        columns={columns}
        getRowId={getRowId}
        pagination
        loading={loadingItems}
        onRowClick={onRowClick}
        pinnedRows={pinnedRows}
        pinnedColumns={pinnedColumns}
        density={withDefault(density, 'standard')}
        columnGroupingModel={columnGroupingModel}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={resolvedPageSizeOptions}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        sx={{
          '& .MuiDataGrid-pinnedRows': {
            boxShadow: 'none !important',
            backgroundColor: 'common.white',
          },
          '& .MuiDataGrid-pinnedRows--bottom': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'common.white',
          },
          ...suppressPinnedColumnSeparatorSx,
          ...hideColumnSeparatorsSx,
        }}
        slots={{
          noRowsOverlay: EmptyTableOverlay,
          loadingOverlay: NoOpOverlay,
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
          columns: {
            columnVisibilityModel: initialColumnVisibility,
          },
        }}
      />
    </Box>
  );
}

export function LoadingOverlay() {
  return <Spinner size={75} />;
}

function NoOpOverlay() {
  return null;
}

function TableLoadingOverlay() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
      }}
    >
      <Spinner size={75} centerPage={false} />
    </Box>
  );
}
